import { DESIGN_MODE_CUSTOM_CSS_SCRIPT_SELECTOR } from "@/constants";
import { REWARDS_WIDGET_NAMES } from "@/constants/widget-names";
import {
    growaveConfigurationToken,
    localizationInfoProviderToken,
    modalManagerToken,
} from "@/tokens";
import { isShadowRoot } from "@/utils/isShadowRoot/isShadowRoot";
import { TRANSLATION_NAMESPACES } from "@apps/rewards/constants/constants";
import RewardsAmountFormatter from "@apps/rewards/lib/RewardsAmountFormatter/RewardsAmountFormatter";
import RewardsCurrencyFormatter from "@apps/rewards/lib/RewardsCurrencyFormatter";
import RewardsCustomerRewardsDiscountService from "@apps/rewards/modules/CustomerRewards/services/RewardsCustomerRewardsDiscountService";
import RewardsProgramAccountQuery from "@apps/rewards/modules/ProgramAccount/query/RewardsProgramAccountQuery";
import RewardsSpendingRulesQuery from "@apps/rewards/modules/SpendingRules/query/RewardsSpendingRulesQuery";
import RewardsSpendingRulesService from "@apps/rewards/modules/SpendingRules/service/RewardsSpendingRulesService";
import RewardsStorefrontValuesService from "@apps/rewards/modules/StorefrontValues/service/RewardsStorefrontValuesService";
import RewardsDiResolverService from "@apps/rewards/services/RewardsDiResolverService/RewardsDIResolverService";
import CustomizationManager from "@lib/CustomizationManager/CustomizationManager";
import { AuthenticationModule } from "@modules/authentication/AuthenticationModule";
import { CommonModule } from "@modules/common_module/CommonModule";
import CustomerQuery from "@modules/customer_module/queries/CustomerQuery";
import CustomerService from "@modules/customer_module/services/CustomerService";
import { DynamicImportModule } from "@modules/dynamic_import/DynamicImportModule";
import { I18nModule } from "@modules/i18n/I18nModule";
import { MoneyModule } from "@modules/money/MoneyModule";
import { injectable } from "tsyringe";

import type { LoggerInterface } from "@interfaces/LoggerInterface";
import type { WidgetInterface } from "@interfaces/WidgetInterface";

import RewardsWaysToSpendPageWidgetComponent from "./RewardsWaysToSpendPageWidget.svelte";
import type { RewardsWaysToSpendPageWidgetConfig } from "./RewardsWaysToSpendPageWidgetConfig";

@injectable()
class RewardsWaysToSpendPageWidget implements WidgetInterface {
    constructor(
        private readonly config: RewardsWaysToSpendPageWidgetConfig,
        private readonly logger: LoggerInterface
    ) {
        this.logger.debug("RewardsWaysToSpendPageWidget is initialized");
    }

    async init(): Promise<this> {
        const widgetNamespace = TRANSLATION_NAMESPACES.waysToSpendPageWidget;
        const pointsNamespace = TRANSLATION_NAMESPACES.commonCriticalPoints;

        const diResolverService = new RewardsDiResolverService();
        const {
            storefrontValuesService,
            spendingRulesService,
            spendingRulesQuery,
            programAccountQuery,
            customerQuery,
            customerService,
            currencyFormatter,
            amountFormatter,
            modalManager,
            dynamicImportModule,
            i18nModule,
            moneyModule,
            commonModule,
            authenticationModule,
            localizationInfoProvider,
            customizationManager,
            growaveConfiguration,
        } = diResolverService.resolveAll({
            storefrontValuesService: RewardsStorefrontValuesService,
            spendingRulesService: RewardsSpendingRulesService,
            customerRewardsDiscountService:
                RewardsCustomerRewardsDiscountService,
            spendingRulesQuery: RewardsSpendingRulesQuery,
            programAccountQuery: RewardsProgramAccountQuery,
            customerQuery: CustomerQuery,
            customerService: CustomerService,
            currencyFormatter: RewardsCurrencyFormatter,
            amountFormatter: RewardsAmountFormatter,
            modalManager: modalManagerToken,
            dynamicImportModule: DynamicImportModule,
            i18nModule: I18nModule,
            moneyModule: MoneyModule,
            commonModule: CommonModule,
            authenticationModule: AuthenticationModule,
            localizationInfoProvider: localizationInfoProviderToken,
            customizationManager: CustomizationManager,
            growaveConfiguration: growaveConfigurationToken,
        });

        const { i18n, i18nApiLoaderFactory, i18nCriticalLoaderFactory } =
            i18nModule.exports;
        i18n.registerLoader(
            widgetNamespace,
            i18nApiLoaderFactory.getLoader(widgetNamespace)
        );
        i18n.registerLoader(
            pointsNamespace,
            i18nCriticalLoaderFactory.getLoader(pointsNamespace)
        );

        const { htmlSanitizer: sanitizer } = moneyModule.exports;
        const { urlsBuilder, locationController, designModeProvider } =
            commonModule.exports;
        const { authenticationInfoProvider } = authenticationModule.exports;

        const element = this.config.target;
        const shadowRoot = isShadowRoot(
            this.config.isWidgetShadowRootEnabled,
            growaveConfiguration.isShadowRootDisabledGlobally
        )
            ? element.shadowRoot || element.attachShadow({ mode: "open" })
            : null;

        const customizationManagerConfig = {
            widgetName: REWARDS_WIDGET_NAMES.REWARDS_WAYS_TO_SPEND_PAGE_WIDGET,
            placeholder: element,
            shadowRoot,
        };

        customizationManager.injectStylesFromBlockCustomCss({
            element,
            shadowRoot,
            scriptSelector: DESIGN_MODE_CUSTOM_CSS_SCRIPT_SELECTOR,
        });
        customizationManager.injectStylesFromCustomization(
            customizationManagerConfig
        );

        await customizationManager.beforeWidgetInject(
            customizationManagerConfig
        );

        new RewardsWaysToSpendPageWidgetComponent({
            target: shadowRoot || element,
            props: {
                storefrontValuesService,
                spendingRulesService,
                spendingRulesQuery,
                programAccountQuery,
                customerQuery,
                customerService,
                currencyFormatter,
                amountFormatter,
                dynamicImportModule,
                modalManager,
                i18n,
                authenticationInfoProvider,
                localizationInfoProvider,
                urlsBuilder,
                locationController,
                sanitizer,
                namespace: widgetNamespace,
                pointsNamespace,
                showPointsBalance: this.config.showPointsBalance,
                redirectToCartAfterRedeem:
                    this.config.redirectToCartAfterRedeem,
                isDesignMode: designModeProvider.isDesignMode(),
                afterWidgetInject: () => {
                    customizationManager.afterWidgetInject(
                        customizationManagerConfig
                    );
                },
            },
        });

        return this;
    }
}

export default RewardsWaysToSpendPageWidget;
